import 'react-toastify/dist/ReactToastify.css';
import React, { useState, useEffect, useCallback } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import { useSearchParams } from "react-router-dom";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Row, Col, Card, CardBody,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Table, Badge, Spinner, Input, Label, FormGroup, FormFeedback
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DashboardPageHeader, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { inkFor } from "../../utils/themeColor";
import {
    FiUserPlus, FiEdit2, FiTrash2, FiSearch,
    FiCalendar, FiUser, FiBriefcase, FiRefreshCw,
    FiCheckCircle, FiXCircle, FiClock, FiDownload } from "react-icons/fi";
import useBackCloses from "../../hooks/useBackCloses";


const TYPE_COLORS = {
    CUSTOMER: "primary",
    VISITOR: "info",
    "JOB CANDIDATE": "warning",
    VENDOR: "secondary",
};

const VISITOR_TYPES = ["CUSTOMER", "VISITOR", "JOB CANDIDATE", "VENDOR"];

const EMPTY_FORM = {
    visitorName: "",
    visitorType: "VISITOR",
    visitorCompany: "",
    employeeId: "",
    employeeName: "",
    expectedDate: "",
    notes: "",
};

// ─── Format date for display ─────────────────────────────────────────────────
const fmtDate = (d) =>
    d ? new Date(d).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    }) : "—";

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ isArrived, isActive }) => {
    if (!isActive) return <Badge color="danger" pill>Cancelled</Badge>;
    if (isArrived) return <Badge color="success" pill>Arrived</Badge>;
    return <Badge color="warning" pill>Pending</Badge>;
};


const ExpectedVisitors = () => {
    document.title = "Expected Visitors | SSW Technologies";

    const authUser = sessionStorage.getItem("authUser");
    const currentEmp = authUser ? JSON.parse(authUser) : null;
    const currentEmpId = currentEmp?.uid || "";
    const currentEmpName = currentEmp?.data?.fullName || currentEmp?.fullName || "";

    // ── State ──────────────────────────────────────────────────────────────
    const [visitors, setVisitors] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // Round 27 — search/status/date filters mirror into the URL so a
    // filtered view can be shared as a link.
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") || "");
    const [filterStatus, setFilterStatus] = useState(() => searchParams.get("vstatus") || "ALL"); // ALL | PENDING | ARRIVED | CANCELLED
    const [filterDate, setFilterDate] = useState(() => searchParams.get("vdate") || "");       // ISO date string
    useEffect(() => {
        const next = {};
        if (filterStatus && filterStatus !== "ALL") next.vstatus = filterStatus;
        if (filterDate) next.vdate = filterDate;
        setSearchParams(next, { replace: true });
    }, [searchTerm, filterStatus, filterDate]);

    const [modalOpen, setModalOpen] = useState(false);
    // Owner 2026-10-12: Back closes the visitor form, the list stays.
    useBackCloses(modalOpen, () => setModalOpen(false));
    const [deleteModal, setDeleteModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    // ── Fetch all expected visitors ────────────────────────────────────────
    const fetchVisitors = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("ExpectedVisitors");
            setVisitors(Array.isArray(res.data) ? res.data : []);
        } catch {
            toast.error("Failed to load expected visitors.");
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Fetch employees for dropdown ───────────────────────────────────────
    const fetchEmployees = useCallback(async () => {
        try {
            const res = await api.get("Employees/list"); // adjust endpoint as needed
            setEmployees(Array.isArray(res.data) ? res.data : []);
        } catch {
            // If employees endpoint not ready, silently skip
        }
    }, []);

    useEffect(() => {
        fetchVisitors();
        fetchEmployees();
    }, [fetchVisitors, fetchEmployees]);

    // ── Open add modal ─────────────────────────────────────────────────────
    const openAdd = () => {
        setEditingId(null);
        setForm({
            ...EMPTY_FORM,
            employeeId: currentEmpId,
            employeeName: currentEmpName,
            expectedDate: new Date().toISOString().slice(0, 16), // datetime-local default = now
        });
        setErrors({});
        setModalOpen(true);
    };

    // ── Open edit modal ────────────────────────────────────────────────────
    const openEdit = (v) => {
        setEditingId(v.id);
        setForm({
            visitorName: v.visitorName || "",
            visitorType: v.visitorType || "VISITOR",
            visitorCompany: v.visitorCompany || "",
            employeeId: v.employeeId || "",
            employeeName: v.employeeName || "",
            expectedDate: v.expectedDate
                ? new Date(v.expectedDate).toISOString().slice(0, 16)
                : "",
            notes: v.notes || "",
        });
        setErrors({});
        setModalOpen(true);
    };

    // ── Form field change ──────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
        if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));

        // Auto-fill employee name when employee is selected from dropdown
        if (name === "employeeId") {
            const emp = employees.find((em) => String(em.empId) === String(value));
            if (emp) setForm((p) => ({ ...p, employeeName: emp.fullName || emp.empName || "" }));
        }
    };

    // ── Validation ─────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!form.visitorName.trim()) e.visitorName = "Visitor name is required.";
        if (!form.visitorType) e.visitorType = "Please select a visitor type.";
        if (!form.employeeId.trim()) e.employeeId = "Employee to meet is required.";
        if (!form.expectedDate) e.expectedDate = "Expected date & time is required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ── Save (create or update) ────────────────────────────────────────────
    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                visitorName: form.visitorName.trim(),
                visitorType: form.visitorType,
                visitorCompany: form.visitorCompany.trim(),
                employeeId: form.employeeId.trim(),
                employeeName: form.employeeName.trim(),
                expectedDate: new Date(form.expectedDate).toISOString(),
                notes: form.notes.trim(),
                createdBy: currentEmpName || currentEmpId,
            };

            if (editingId) {
                await api.put(`ExpectedVisitors/${editingId}`, payload);
                toast.success("Visitor entry updated.");
            } else {
                await api.post("ExpectedVisitors", payload);
                toast.success("Expected visitor added successfully.");
            }

            setModalOpen(false);
            fetchVisitors();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // ── Confirm delete ─────────────────────────────────────────────────────
    const confirmDelete = (id) => {
        setDeletingId(id);
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        try {
            await api.delete(`ExpectedVisitors/${deletingId}`);
            toast.success("Entry deleted.");  // Round GEN-3 — true delete now
            setDeleteModal(false);
            fetchVisitors();
        } catch (err) {
            // Go-live parity (2026-09-24): show the server's reason (e.g. only
            // the author, the host, HR or IT may delete an entry).
            toast.error(err?.response?.data?.message || "Failed to delete entry.");
        }
    };

    // ── Filtered list ──────────────────────────────────────────────────────
    // Round 34 — EPO-style per-column filters + click-to-sort. The status
    // and date dropdowns stay (quick scopes); the free-text search box is
    // replaced by the filter row.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = React.useMemo(() => [
        { id: "idx",        label: "#",            type: "none" },
        { id: "visitorName",   label: "Visitor Name",  type: "text", get: (v) => v.visitorName || "" },
        { id: "visitorType",   label: "Type",          type: "text", get: (v) => v.visitorType || "" },
        { id: "visitorCompany",label: "Company",       type: "text", get: (v) => v.visitorCompany || "" },
        { id: "employeeName",  label: "Host Employee", type: "text", get: (v) => v.employeeName || "" },
        { id: "expectedDate",  label: "Expected On",   type: "date", get: (v) => v.expectedDate || "" },
        { id: "status",        label: "Status",        type: "text", get: (v) => (!v.isActive ? "Cancelled" : v.isArrived ? "Arrived" : "Pending") },
        { id: "notes",         label: "Notes",         type: "text", get: (v) => v.notes || "" },
        { id: "createdBy",     label: "Added By",      type: "text", get: (v) => v.createdBy || "" },
        { id: "act",           label: "Actions",       type: "none" },
    ], []);
    const filtered = visitors.filter((v) => {
        const matchSearch =
            v.visitorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.visitorCompany?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.employeeName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchStatus =
            filterStatus === "ALL" ? true :
                filterStatus === "PENDING" ? (!v.isArrived && v.isActive) :
                    filterStatus === "ARRIVED" ? v.isArrived :
                        filterStatus === "CANCELLED" ? !v.isActive : true;

        const matchDate = filterDate
            ? new Date(v.expectedDate).toDateString() === new Date(filterDate).toDateString()
            : true;

        return matchSearch && matchStatus && matchDate;
    });

    // ─────────────────────────────────────────────────────────────────────
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-user-received-line"
                        eyebrow="Front desk"
                        title="Expected Visitors"
                        subtitle="Log and track visitors expected on site."
                        tone="teal"
                        breadcrumb={[
                            { label: "Dashboards", to: "/" },
                            { label: "Expected Visitors" },
                        ]}
                    />

                    {/* ── Stats strip ─────────────────────────────────────── */}
                    <Row className="mb-3 g-3">
                        {[
                            { label: "Total", value: visitors.length, icon: <FiUser />, color: "#534AB7" },
                            { label: "Pending", value: visitors.filter(v => !v.isArrived && v.isActive).length, icon: <FiClock />, color: "#b45309" },
                            { label: "Arrived", value: visitors.filter(v => v.isArrived).length, icon: <FiCheckCircle />, color: "#198754" },
                            { label: "Cancelled", value: visitors.filter(v => !v.isActive).length, icon: <FiXCircle />, color: "#dc3545" },
                        ].map((s) => (
                            <Col xs={6} md={3} key={s.label}>
                                <Card className="h-100" style={{ borderTop: `3px solid ${s.color}` }}>
                                    <CardBody style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px" }}>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: 10,
                                            background: s.color + "18",
                                            color: inkFor(s.color),
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 18, flexShrink: 0
                                        }}>{s.icon}</div>
                                        <div>
                                            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
                                            <div style={{ fontSize: 12, color: "var(--ssw-fg-2, #888)", marginTop: 2 }}>{s.label}</div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Table card ──────────────────────────────────────── */}
                    <Row>
                        <Col xs={12} style={{ minWidth: 0 }}>
                            <Card>
                                <CardBody>

                                    {/* Toolbar */}
                                    <div style={{
                                        display: "flex", flexWrap: "wrap",
                                        gap: 10, alignItems: "center",
                                        marginBottom: 16
                                    }}>
                                        {/* Search */}
                                        {/* Round 34 — search replaced by the per-column filter row */}

                                        {/* Status filter */}
                                        <select
                                            value={filterStatus}
                                            onChange={(e) => setFilterStatus(e.target.value)}
                                            style={{
                                                height: 36, borderRadius: 8, border: "1px solid var(--ssw-line, #ddd)",
                                                padding: "0 10px", fontSize: 13, minWidth: 130, outline: "none"
                                            }}
                                        >
                                            {["ALL", "PENDING", "ARRIVED", "CANCELLED"].map(s => (
                                                <option key={s} value={s}>{s === "ALL" ? "All Statuses" : s}</option>
                                            ))}
                                        </select>

                                        {/* Date filter */}
                                        <input
                                            type="date"
                                            value={filterDate}
                                            onChange={(e) => setFilterDate(e.target.value)}
                                            style={{
                                                height: 36, borderRadius: 8, border: "1px solid var(--ssw-line, #ddd)",
                                                padding: "0 10px", fontSize: 13, outline: "none"
                                            }}
                                        />

                                        <Button
                                            size="sm" color="light"
                                            onClick={fetchVisitors}
                                            title="Refresh"
                                            style={{ height: 36, borderRadius: 8, padding: "0 12px" }}
                                        >
                                            <FiRefreshCw size={14} />
                                        </Button>

                                        {/* Add button */}
                                        <Button
                                            onClick={openAdd}
                                            style={{
                                                height: 36, borderRadius: 8,
                                                background: "var(--ssw-primary-fill, #534AB7)", border: "none",
                                                fontSize: 13, fontWeight: 600,
                                                display: "flex", alignItems: "center", gap: 6,
                                                marginLeft: "auto"
                                            }}
                                        >
                                            <FiUserPlus size={14} /> Add Expected Visitor
                                        </Button>
                                        {/* Round 27 — CSV of the filtered list */}
                                        <Button
                                            size="sm"
                                            color="light"
                                            onClick={() =>
                                                exportRowsToCsv("expected-visitors.csv", [
                                                    { key: "visitorName",    label: "Visitor" },
                                                    { key: "visitorCompany", label: "Company" },
                                                    { key: "visitorType",    label: "Type" },
                                                    { key: "employeeName",   label: "Host" },
                                                    { key: "expectedDate",   label: "Expected" },
                                                    { label: "Status", get: (v) => (!v.isActive ? "Cancelled" : v.isArrived ? "Arrived" : "Pending") },
                                                    { key: "notes",          label: "Notes" },
                                                    { key: "createdBy",      label: "Created By" },
                                                ], view)
                                            }
                                        >
                                            <FiDownload size={14} /> Export CSV ({view.length})
                                        </Button>
                                    </div>

                                    {/* Table */}
                                    {loading ? (
                                        <div className="table-responsive">
                                            <table className="table align-middle mb-0">
                                                <tbody><TableSkeleton rows={8} columns={11} /></tbody>
                                            </table>
                                        </div>
                                    ) : filtered.length === 0 ? (
                                        <div style={{
                                            textAlign: "center", padding: "40px 20px",
                                            color: "var(--ssw-fg-2, #999)", fontSize: 14
                                        }}>
                                            <FiUser size={32} style={{ marginBottom: 10, opacity: 0.3 }} />
                                            <div>No expected visitors found.</div>
                                        </div>
                                    ) : (
                                        <div className="table-responsive" style={{ width: "100%", maxWidth: "100%" }}>
                                            <Table hover bordered className="mb-0" style={{ fontSize: 13, marginBottom: 0 }}>
                                                <thead style={{ background: "var(--ssw-surface-3, #f8f9fa)" }}>
                                                    <tr>
                                                        {tblColumns.map((c) => (
                                                            <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort}
                                                                className={c.id === "act" ? "text-center" : ""} />
                                                        ))}
                                                    </tr>
                                                    <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                                </thead>
                                                <tbody>
                                                    {view.map((v, idx) => (
                                                        <tr key={v.id} style={{ opacity: !v.isActive ? 0.55 : 1 }}>
                                                            <td style={{ color: "var(--ssw-fg-2, #999)" }}>{idx + 1}</td>
                                                            <td style={{ fontWeight: 600 }}>{v.visitorName}</td>
                                                            <td>
                                                                <Badge color={TYPE_COLORS[v.visitorType] || "secondary"} pill>
                                                                    {v.visitorType}
                                                                </Badge>
                                                            </td>
                                                            <td>{v.visitorCompany || "—"}</td>
                                                            <td>{v.employeeName || v.employeeId}</td>
                                                            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(v.expectedDate)}</td>
                                                            <td>
                                                                <StatusBadge isArrived={v.isArrived} isActive={v.isActive} />
                                                            </td>
                                                            <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                <span title={v.notes}>{v.notes || "—"}</span>
                                                            </td>
                                                            <td style={{ color: "var(--ssw-fg-2, #777)" }}>{v.createdBy || "—"}</td>
                                                            <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                                <Button
                                                                    size="sm" color="light"
                                                                    onClick={() => openEdit(v)}
                                                                    disabled={!v.isActive || v.isArrived}
                                                                    title="Edit"
                                                                    style={{ marginRight: 6, borderRadius: 6 }}
                                                                >
                                                                    <FiEdit2 size={13} />
                                                                </Button>
                                                                <Button
                                                                    size="sm" color="light"
                                                                    onClick={() => confirmDelete(v.id)}
                                                                    disabled={!v.isActive}
                                                                    title="Delete"
                                                                    style={{ borderRadius: 6, color: "var(--ssw-ink-red, #dc3545)" }}
                                                                >
                                                                    <FiTrash2 size={13} />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Record count */}
                                    {!loading && filtered.length > 0 && (
                                        <div style={{ fontSize: 12, color: "var(--ssw-fg-3, #aaa)", marginTop: 10 }}>
                                            Showing {filtered.length} of {visitors.length} record{visitors.length !== 1 ? "s" : ""}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                </Container>
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                ADD / EDIT MODAL
            ═══════════════════════════════════════════════════════════════ */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered size="lg" modalClassName="flip">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    <FiUserPlus className="me-2" />
                    {editingId ? "Edit Expected Visitor" : "Add Expected Visitor"}
                </ModalHeader>

                <ModalBody>
                    <Row className="g-3">

                        {/* Visitor Name */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Visitor Name <span style={{ color: "var(--ssw-ink-red, #dc3545)" }}>*</span></Label>
                                <Input
                                    name="visitorName"
                                    placeholder="Full name of the visitor"
                                    value={form.visitorName}
                                    onChange={handleChange}
                                    invalid={!!errors.visitorName}
                                />
                                <FormFeedback>{errors.visitorName}</FormFeedback>
                            </FormGroup>
                        </Col>

                        {/* Visitor Type */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Visitor Type <span style={{ color: "var(--ssw-ink-red, #dc3545)" }}>*</span></Label>
                                <Input
                                    type="select"
                                    name="visitorType"
                                    value={form.visitorType}
                                    onChange={handleChange}
                                    invalid={!!errors.visitorType}
                                >
                                    {VISITOR_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.visitorType}</FormFeedback>
                            </FormGroup>
                        </Col>

                        {/* Company */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Company / Organization</Label>
                                <Input
                                    name="visitorCompany"
                                    placeholder="Visitor's company (optional)"
                                    value={form.visitorCompany}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>

                        {/* Host Employee */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Employee to Meet <span style={{ color: "var(--ssw-ink-red, #dc3545)" }}>*</span></Label>
                                {employees.length > 0 ? (
                                    <Input
                                        type="select"
                                        name="employeeId"
                                        value={form.employeeId}
                                        onChange={handleChange}
                                        invalid={!!errors.employeeId}
                                    >
                                        <option value="">-- Select Employee --</option>
                                        {employees.map((e) => (
                                            <option key={e.empId} value={e.empId}>
                                                {e.fullName || e.empName}
                                            </option>
                                        ))}
                                    </Input>
                                ) : (
                                    <Input
                                        name="employeeId"
                                        placeholder="Employee ID"
                                        value={form.employeeId}
                                        onChange={handleChange}
                                        invalid={!!errors.employeeId}
                                    />
                                )}
                                <FormFeedback>{errors.employeeId}</FormFeedback>
                            </FormGroup>
                        </Col>

                        {/* Expected Date & Time */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Expected Date & Time <span style={{ color: "var(--ssw-ink-red, #dc3545)" }}>*</span></Label>
                                <Input
                                    type="datetime-local"
                                    name="expectedDate"
                                    value={form.expectedDate}
                                    onChange={handleChange}
                                    invalid={!!errors.expectedDate}
                                />
                                <FormFeedback>{errors.expectedDate}</FormFeedback>
                            </FormGroup>
                        </Col>

                        {/* Notes */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Notes</Label>
                                <Input
                                    type="textarea"
                                    name="notes"
                                    placeholder="Purpose of visit, special instructions..."
                                    value={form.notes}
                                    onChange={handleChange}
                                    rows={3}
                                    style={{ resize: "none" }}
                                />
                            </FormGroup>
                        </Col>

                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        style={{ background: "var(--ssw-primary-fill, #534AB7)", border: "none", borderRadius: 8, minWidth: 110 }}
                    >
                        {saving
                            ? <><Spinner size="sm" className="me-2" />Saving...</>
                            : editingId ? "Update" : "Add Visitor"
                        }
                    </Button>
                    <Button color="secondary" onClick={() => setModalOpen(false)} style={{ borderRadius: 8 }}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ═══════════════════════════════════════════════════════════════
                DELETE CONFIRM MODAL
            ═══════════════════════════════════════════════════════════════ */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
                <ModalBody className="text-center p-5">
                    <div style={{
                        width: 64, height: 64, borderRadius: "50%",
                        background: "var(--ssw-tint-red, #fde8e8)", margin: "0 auto 16px",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <FiTrash2 size={28} color="#dc3545" />
                    </div>
                    <h5>Delete this entry?</h5>
                    <p className="text-muted">
                        This will permanently delete the expected-visitor entry. This action cannot be undone.
                    </p>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
                        <Button color="danger" onClick={handleDelete} style={{ borderRadius: 8, minWidth: 100 }}>
                            Yes, Delete
                        </Button>
                        <Button color="light" onClick={() => setDeleteModal(false)} style={{ borderRadius: 8, minWidth: 100 }}>
                            Go Back
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

        </React.Fragment>
    );
};

export default ExpectedVisitors;